/**
 * _core.ts — shared HTTP plumbing for every per-feature api/*.ts module
 * (PR-15 split of the 1311-line monolith api/client.ts).
 *
 * Owns the single `call<T>()` fetch wrapper, the `ApiCallError` class, the
 * session-token helpers, and the device-timezone helper. Per-feature modules
 * (auth.ts, pets.ts, shop.ts, …) import `call`, `getTz`, `deviceTz` from
 * here so the wrapper logic — auth header, content-type defaulting, single
 * GET retry, error shaping — exists in exactly one place.
 *
 * 调用关系：
 *   - Imported by every other file in frontend/src/api/ (auth, me, pets,
 *     shop, zoo, chat, …) and re-exported through api/client.ts as the
 *     public surface (`ApiCallError`, `getToken`, `setSession`,
 *     `clearSession`).
 *   - Sibling api/scheduleClient.ts imports `ApiCallError` from
 *     api/client.ts (which re-exports it from here) to keep one
 *     catch-shape across the kid app.
 *   - Backend endpoint base + session-token storage keys come from
 *     `@xiaopeiban/shared`.
 *
 * 关键约束：
 *   - GET requests are auto-retried once after RETRY_DELAY_MS on network
 *     failure; non-GET requests fail fast (we don't want to double-write
 *     coins / chores).
 *   - SESSION_TYPE_KEY in localStorage is shared with the parent admin at
 *     /parent/* — writing 'parent' here means the parent admin will pick
 *     up the same session on reload.
 *   - resetDeviceScopedSessionState() runs on setSession + clearSession so
 *     the play-session accumulator doesn't carry across account switches.
 *   - Device IANA tz is sent on every "today"-style endpoint so daily
 *     resets align with the kid's local clock instead of the backend's
 *     hardcoded Asia/Kuala_Lumpur default.
 */
import {
  DEFAULT_API_BASE,
  SESSION_TOKEN_KEY,
  SESSION_TYPE_KEY,
} from '@xiaopeiban/shared';
import type { ApiError } from '@xiaopeiban/shared';
import { getLang } from '../i18n';

// Retry delay (ms) between the initial GET request and its single retry on
// network failure. Tuned to feel "snappy enough" to the kid while still
// giving e.g. a flapping Wi-Fi handoff time to settle.
const RETRY_DELAY_MS = 650;

// Resolved at module load — VITE_API_BASE overrides the shared default when
// the test stack deploys against the test Worker.
export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || DEFAULT_API_BASE;

export function getToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function setSession(token: string, type: 'parent' | 'child'): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
  localStorage.setItem(SESSION_TYPE_KEY, type);
  // Play-session counters live in localStorage and are device-scoped, not
  // account-scoped — so without this reset a freshly-hatched kid inherits
  // accumulated minutes from whoever last used this browser. Wipe on
  // every fresh sign-in so each account starts the clock at zero.
  resetDeviceScopedSessionState();
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_TYPE_KEY);
  resetDeviceScopedSessionState();
}

/**
 * Clear localStorage state that's tied to the current device's "play
 * session" rather than the logged-in account: the play-time accumulator,
 * the period anchor, and the last-tick timestamp. Period TYPE/HOURS
 * preferences (set by the parent admin) are intentionally preserved —
 * those are device-level UX choices, not session counters.
 *
 * The PWA install nudge snooze/dismiss flags are ALSO preserved — same
 * reasoning: a returning kid on the same device shouldn't get re-nagged
 * just because they logged out and back in.
 */
function resetDeviceScopedSessionState(): void {
  try {
    localStorage.removeItem('xiaopeiban_play_session_start');
    localStorage.removeItem('xiaopeiban_play_accumulated_ms');
    localStorage.removeItem('xiaopeiban_play_last_tick_at');
  } catch { /* private mode etc — ignore */ }
}

/**
 * IANA timezone the client is currently in — passed to any backend
 * endpoint that keys off "today" / time-of-day so that daily resets and
 * morning/night windows match the kid's actual local clock instead of
 * the backend's hardcoded Asia/Kuala_Lumpur fallback. Exposed under two
 * names (`getTz` + `deviceTz`) because the pre-split code used both
 * interchangeably; kept both to avoid touching call sites.
 */
export function deviceTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kuala_Lumpur';
  } catch {
    return 'Asia/Kuala_Lumpur';
  }
}
export const getTz = deviceTz;

export class ApiCallError extends Error {
  code: string;
  status: number;
  detail?: unknown;
  constructor(code: string, message: string, status: number, detail?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

/**
 * The one and only fetch wrapper used by every per-feature api module.
 * Adds the bearer token, defaults Content-Type, retries GETs once on
 * network failure, and re-shapes both HTTP and `{ error: {...} }` bodies
 * into `ApiCallError` so callers can use a single try/catch shape.
 */
export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const url = `${API_BASE}${path}`;
  const method = String(init.method || 'GET').toUpperCase();
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (e) {
    if (method === 'GET') {
      await new Promise((resolve) => globalThis.setTimeout(resolve, RETRY_DELAY_MS));
      try {
        res = await fetch(url, { ...init, headers });
      } catch (retryError) {
        throw networkError(retryError);
      }
    } else {
      throw networkError(e);
    }
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data as ApiError).error) {
    const e = (data as ApiError).error;
    throw new ApiCallError(
      e?.code || 'UNKNOWN',
      e?.message || `HTTP ${res.status}`,
      res.status,
      e?.detail,
    );
  }
  return data as T;
}

function networkError(e: unknown): ApiCallError {
  const lang = getLang();
  const fallback = lang === 'zh'
    ? '网络不稳定，请再试一次'
    : 'Network is unstable. Please try again.';
  const raw = e instanceof Error ? e.message : '';
  return new ApiCallError('NETWORK_ERROR', raw && raw !== 'Load failed' ? raw : fallback, 0);
}

// Re-exported so per-feature modules that need the active UI language for
// `?lang=` query strings (chat, quiz, companion) don't have to import from
// '../i18n' separately — one import line per file stays tidy.
export { getLang };
